import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { adminApi, userApi, type AdminSessionResponse, type UserMeResponse } from "./api";
import { ADMIN_BASE_PATH } from "./admin-path";
import { subscribeCreditChanges } from "./credit-interactions";

type LoadState = "loading" | "authenticated" | "anonymous";

interface UserAuthValue {
  state: LoadState;
  me?: UserMeResponse;
  refresh(): Promise<void>;
  requestPhoneCode(phone: string, captchaPayload: unknown, website: string): Promise<{ expiresInSeconds: number; resendAfterSeconds: number }>;
  loginWithPhone(phone: string, code: string): Promise<{ isNewUser: boolean }>;
  loginWithPassword(phone: string, password: string): Promise<void>;
  devLogin(): Promise<void>;
  logout(): Promise<void>;
  requestAccountClosure(): Promise<void>;
}

interface AdminAuthValue {
  state: LoadState;
  auth?: AdminSessionResponse;
  refresh(): Promise<void>;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  can(permission: string): boolean;
}

const UserAuthContext = createContext<UserAuthValue | null>(null);
const AdminAuthContext = createContext<AdminAuthValue | null>(null);

export function UserAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LoadState>("loading");
  const [me, setMe] = useState<UserMeResponse>();

  const refresh = async () => {
    const token = localStorage.getItem("pptdeep_user_token") || localStorage.getItem("df_ai_ppt_token");
    if (!token) {
      setMe(undefined);
      setState("anonymous");
      return;
    }
    try {
      setMe(await userApi<UserMeResponse>("/api/app/me"));
      setState("authenticated");
    } catch {
      localStorage.removeItem("pptdeep_user_token");
      localStorage.removeItem("df_ai_ppt_token");
      setMe(undefined);
      setState("anonymous");
    }
  };

  useEffect(() => { void refresh(); }, []);
  useEffect(() => subscribeCreditChanges(() => { void refresh(); }), []);

  const value = useMemo<UserAuthValue>(() => ({
    state,
    me,
    refresh,
    requestPhoneCode: async (phone, captchaPayload, website) => userApi("/api/app/auth/phone-code/send", {
      method: "POST",
      body: JSON.stringify({ phone, captchaPayload, website }),
    }),
    loginWithPhone: async (phone, code) => {
      const data = await userApi<{ token: string; isNewUser?: boolean }>("/api/app/auth/phone-code", {
        method: "POST",
        body: JSON.stringify({ phone, code }),
      });
      localStorage.setItem("pptdeep_user_token", data.token);
      await refresh();
      return { isNewUser: data.isNewUser === true };
    },
    loginWithPassword: async (phone, password) => {
      const data = await userApi<{ token: string }>("/api/app/auth/login", {
        method: "POST",
        body: JSON.stringify({ phone, password }),
      });
      localStorage.setItem("pptdeep_user_token", data.token);
      await refresh();
    },
    devLogin: async () => {
      const data = await userApi<{ token: string }>("/api/app/auth/dev-login", {
        method: "POST",
        body: JSON.stringify({ nickname: "幻视PPT Tester" }),
      });
      localStorage.setItem("pptdeep_user_token", data.token);
      await refresh();
    },
    logout: async () => {
      try { await userApi("/api/app/auth/logout", { method: "POST", body: "{}" }); } catch { /* local cleanup still applies */ }
      localStorage.removeItem("pptdeep_user_token");
      localStorage.removeItem("df_ai_ppt_token");
      setMe(undefined);
      setState("anonymous");
    },
    requestAccountClosure: async () => {
      await userApi("/api/app/account/closure", { method: "POST", body: JSON.stringify({ confirm: true }) });
      localStorage.removeItem("pptdeep_user_token");
      localStorage.removeItem("df_ai_ppt_token");
      setMe(undefined);
      setState("anonymous");
    },
  }), [state, me]);

  return <UserAuthContext.Provider value={value}>{children}</UserAuthContext.Provider>;
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<LoadState>("loading");
  const [auth, setAuth] = useState<AdminSessionResponse>();

  const refresh = async () => {
    try {
      setAuth(await adminApi<AdminSessionResponse>("/api/admin/session"));
      setState("authenticated");
    } catch {
      setAuth(undefined);
      setState("anonymous");
    }
  };

  useEffect(() => {
    if (location.pathname === ADMIN_BASE_PATH || location.pathname.startsWith(`${ADMIN_BASE_PATH}/`)) {
      void refresh();
      return;
    }
    setAuth(undefined);
    setState("anonymous");
  }, [location.pathname]);

  const value = useMemo<AdminAuthValue>(() => ({
    state,
    auth,
    refresh,
    login: async (username, password) => {
      await adminApi("/api/admin/login", { method: "POST", body: JSON.stringify({ username, password }) });
      await refresh();
    },
    logout: async () => {
      try { await adminApi("/api/admin/logout", { method: "POST", body: "{}" }); } finally {
        setAuth(undefined);
        setState("anonymous");
      }
    },
    can: (permission) => auth?.permissions.includes(permission) === true,
  }), [state, auth]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useUserAuth(): UserAuthValue {
  const value = useContext(UserAuthContext);
  if (!value) throw new Error("UserAuthProvider is missing");
  return value;
}

export function useAdminAuth(): AdminAuthValue {
  const value = useContext(AdminAuthContext);
  if (!value) throw new Error("AdminAuthProvider is missing");
  return value;
}
